import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Sparkles, TrendingUp, TrendingDown } from "lucide-react";

export default function MarketSummary() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/tools/market-summary")
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  const timeAgo = (iso) => {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (diff < 1) return "just now";
    if (diff < 60) return diff + "m ago";
    return Math.floor(diff / 60) + "h ago";
  };

  return (
    <div className="pt-28 pb-20 max-w-2xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-10">
        <Sparkles className="w-8 h-8 text-yellow-400 mx-auto mb-4" />
        <h1 className="font-display font-bold text-4xl text-[var(--text-primary)]">AI Market Summary</h1>
        <p className="mt-3 text-[var(--text-secondary)]">
          An AI-generated recap of current market conditions, refreshed hourly.
        </p>
      </div>

      {error ? (
        <p className="text-center text-red-400 font-mono text-sm">Could not load market summary. Try again shortly.</p>
      ) : !data ? (
        <p className="text-center text-[var(--text-tertiary)] font-mono text-sm">Loading summary...</p>
      ) : (
        <>
          <div className="bg-[var(--bg-panel)] border border-yellow-400/20  p-6">
            <p className="text-[var(--text-primary)] leading-relaxed">{data.summary}</p>
            <p className="text-xs font-mono text-[var(--text-tertiary)] mt-4 pt-4 border-t border-[var(--border-soft)]">
              Generated {timeAgo(data.generated_at)} · Not financial advice
            </p>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.market_data.coins.map((c) => (
              <div key={c.symbol} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-4 flex items-center justify-between">
                <span className="font-mono font-bold text-[var(--text-primary)]">{c.symbol}</span>
                <div className="text-right">
                  <div className="font-mono text-sm text-[var(--text-primary)]">${c.price.toLocaleString()}</div>
                  <div className={"text-xs font-mono flex items-center gap-1 justify-end " + (c.change_24h >= 0 ? "text-green-400" : "text-red-400")}>
                    {c.change_24h >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {c.change_24h >= 0 ? "+" : ""}{c.change_24h.toFixed(2)}%
                  </div>
                </div>
              </div>
            ))}
          </div>

          {data.market_data.fear_greed && (
            <p className="text-center text-xs font-mono text-[var(--text-tertiary)] mt-6">
              Fear & Greed Index: {data.market_data.fear_greed.value} ({data.market_data.fear_greed.label})
            </p>
          )}
        </>
      )}

      <div className="mt-16 border-t border-[var(--border-soft)] pt-10">
        <h2 className="font-display font-bold text-2xl text-[var(--text-primary)] mb-6">How this summary works</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">1. Gather live data</div>
            <p className="text-xs text-[var(--text-secondary)]">Current prices, 24 hour changes, and trading volume are pulled for the top coins, along with the Fear & Greed Index.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">2. AI writes a recap</div>
            <p className="text-xs text-[var(--text-secondary)]">An AI model reads that data and writes a short, plain-language summary of the overall market mood and biggest movers.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">3. Refreshed hourly</div>
            <p className="text-xs text-[var(--text-secondary)]">The summary regenerates once per hour, so it always reflects a recent market snapshot.</p>
          </div>
        </div>

        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
          This summary is generated from live price and volume data only. It does not analyze news, on-chain activity, or fundamentals, and should not be treated as financial advice or a trading signal. Always verify current prices independently before making decisions.
        </p>
      </div>
    </div>
  );
}